import { apiProvider, apiClient } from './utilities/Provider';

/**
 * Admin "Clients" API. A client is a company: one account Owner row that carries the company
 * fields, plus the users invited under it. The list endpoint groups, searches, filters, sorts
 * and paginates server-side and returns companies with their (matching) members in one response.
 */

const getCompanies = (params = {}) => {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.companyStatus && params.companyStatus !== 'All') query.set('companyStatus', params.companyStatus);
    if (params.userStatus && params.userStatus !== 'All') query.set('userStatus', params.userStatus);
    if (params.role && params.role !== 'All') query.set('role', params.role);
    if (params.sort) query.set('sort', params.sort);
    query.set('page', String(params.page ?? 1));
    query.set('pageSize', String(params.pageSize ?? 20));

    return apiProvider.get(`agency/owners?${query.toString()}`).then((data) => {
        // Tolerate the pre-companies API shape (a bare owner array) during a rollout window:
        // render an empty page instead of crashing until the backend is deployed.
        if (Array.isArray(data)) return { items: [], totalCount: 0, page: 1, pageSize: params.pageSize ?? 20 };
        return {
            items: data?.items ?? [],
            totalCount: data?.totalCount ?? 0,
            page: data?.page ?? 1,
            pageSize: data?.pageSize ?? (params.pageSize ?? 20),
        };
    });
};

const getById = (id) => apiProvider.get(`agency/owners/${id}`);

// The Owner row plus every user invited under the company — for the client-detail "Users" section.
const getMembers = (ownerId) => apiProvider.get(`agency/owners/${ownerId}/members`);

// Add Client == create the company together with its first owner. Only email + name are
// required; the rest of the owner / company / billing profile is optional.
const createCompany = (payload) =>
    apiProvider.post('agency/owners', {
        email: payload.email,
        firstName: payload.firstName,
        lastName: payload.lastName,
        phoneNumber: payload.phoneNumber,
        additionalInfo: payload.additionalInfo,
        companyName: payload.companyName,
        companyNip: payload.companyNip,
        companyWebsite: payload.companyWebsite,
        billingStreet: payload.billingStreet,
        billingBuildingNumber: payload.billingBuildingNumber,
        billingPostalCode: payload.billingPostalCode,
        billingCity: payload.billingCity,
        billingCountry: payload.billingCountry,
    });

// One form, company + owner data together.
const updateCompany = (ownerId, payload) =>
    apiProvider.put(`agency/owners/${ownerId}`, {
        firstName: payload.firstName,
        lastName: payload.lastName,
        isActive: payload.isActive,
        phoneNumber: payload.phoneNumber,
        additionalInfo: payload.additionalInfo,
        companyName: payload.companyName,
        companyNip: payload.companyNip,
        companyWebsite: payload.companyWebsite,
        billingStreet: payload.billingStreet,
        billingBuildingNumber: payload.billingBuildingNumber,
        billingPostalCode: payload.billingPostalCode,
        billingCity: payload.billingCity,
        billingCountry: payload.billingCountry,
    });

const deactivateCompany = (ownerId) => apiProvider.post(`agency/owners/${ownerId}/deactivate`, {});
const activateCompany = (ownerId) => apiProvider.post(`agency/owners/${ownerId}/activate`, {});

const addMember = (ownerId, { email, firstName, lastName }) =>
    apiProvider.post(`agency/owners/${ownerId}/users`, { email, firstName, lastName });

const updateMember = (userId, { firstName, lastName, email }) =>
    apiProvider.put(`agency/users/${userId}`, { firstName, lastName, email });

const deactivateMember = (userId) => apiProvider.post(`agency/users/${userId}/deactivate`, {});
const activateMember = (userId) => apiProvider.post(`agency/users/${userId}/activate`, {});
const resendInvitation = (userId) => apiProvider.post(`agency/users/${userId}/resend-invitation`, {});
// apiClient so errors reject.
const resetMemberTwoFactor = (userId) => apiClient.post(`/agency/users/${userId}/reset-two-factor`, {});

const ClientService = {
    getCompanies,
    getById,
    getMembers,
    createCompany,
    updateCompany,
    deactivateCompany,
    activateCompany,
    addMember,
    updateMember,
    deactivateMember,
    activateMember,
    resendInvitation,
    resetMemberTwoFactor,
};

export default ClientService;
